import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Calendar, MessageSquare, TrendingUp, LogOut, RefreshCw, Settings, Phone, Video, Save, Plus, X, Globe, Shield, Check, XCircle, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [commissions, setCommissions] = useState({});
  const [overrides, setOverrides] = useState([]);
  const [newOverride, setNewOverride] = useState({ industry: '', country: '', commission_dm: '', commission_voice_note: '', commission_voice_call: '', commission_video_call: '' });
  const [showAddOverride, setShowAddOverride] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [verifyStats, setVerifyStats] = useState({});
  const [previewDoc, setPreviewDoc] = useState(null);
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { if (!token) { navigate('/login'); return; } fetchStats(); fetchVerifications(); }, []);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/stats`, { headers });
      if (res.ok) {
        const d = await res.json();
        setStats(d);
        setCommissions(d.platform_settings || {});
        setOverrides(d.commission_overrides || []);
      } else if (res.status === 403) { alert('Admin access required'); navigate('/app'); }
    } catch {}
    setLoading(false);
  };

  const saveGlobal = async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/admin/commissions`, { method: 'PUT', headers, body: JSON.stringify({
        commission_dm: commissions.commission_dm, commission_voice_note: commissions.commission_voice_note,
        commission_voice_call: commissions.commission_voice_call, commission_video_call: commissions.commission_video_call,
        referral_percentage: commissions.referral_percentage,
      })});
      alert('Global commissions saved!');
    } catch {}
    setSaving(false);
  };

  const addOverride = async () => {
    if (!newOverride.industry) return;
    try {
      const body = { ...newOverride };
      ['commission_dm', 'commission_voice_note', 'commission_voice_call', 'commission_video_call'].forEach(k => {
        body[k] = body[k] ? parseFloat(body[k]) : undefined;
      });
      await fetch(`${API_URL}/api/admin/commissions/override`, { method: 'PUT', headers, body: JSON.stringify(body) });
      setShowAddOverride(false);
      setNewOverride({ industry: '', country: '', commission_dm: '', commission_voice_note: '', commission_voice_call: '', commission_video_call: '' });
      fetchStats();
    } catch {}
  };

  const deleteOverride = async (o) => {
    await fetch(`${API_URL}/api/admin/commissions/override?industry=${encodeURIComponent(o.industry)}&country=${encodeURIComponent(o.country || '')}`, { method: 'DELETE', headers });
    fetchStats();
  };

  const fetchVerifications = async () => {
    try {
      const res = await fetch(`${API_URL}/api/verify/pending-list`, { headers });
      if (res.ok) {
        const d = await res.json();
        setPendingVerifications(d.pending_users || []);
        setVerifyStats(d.stats || {});
      }
    } catch {}
  };

  const approveUser = async (userId) => {
    try {
      await fetch(`${API_URL}/api/verify/approve/${userId}`, { method: 'POST', headers });
      fetchVerifications();
    } catch {}
  };

  const rejectUser = async (userId) => {
    try {
      await fetch(`${API_URL}/api/verify/reject/${userId}`, { method: 'POST', headers });
      fetchVerifications();
    } catch {}
  };

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center"><div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" /></div>;

  const FIELDS = [
    { key: 'commission_dm', label: 'Paid DM', icon: MessageSquare, color: 'text-blue-400' },
    { key: 'commission_voice_note', label: 'Voice Note', icon: Phone, color: 'text-green-400' },
    { key: 'commission_voice_call', label: 'Voice Call', icon: Phone, color: 'text-purple-400' },
    { key: 'commission_video_call', label: 'Video Call', icon: Video, color: 'text-orange-400' },
  ];

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-dashboard">
      <nav className="bg-black border-b border-zinc-800 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-orange-500 rounded-lg flex items-center justify-center"><span className="text-white font-bold text-sm">C</span></div>
          <span className="font-bold text-lg">Connect Admin</span>
          <span className="bg-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">ADMIN</span>
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={fetchStats} className="text-zinc-400 hover:text-white" data-testid="refresh-button"><RefreshCw className="w-5 h-5" /></button>
          <button onClick={() => { localStorage.removeItem('token'); navigate('/login'); }} className="text-zinc-400 hover:text-red-400" data-testid="logout-button"><LogOut className="w-5 h-5" /></button>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <h1 className="text-2xl font-bold mb-6" data-testid="admin-title">Admin Dashboard</h1>

        <div className="flex space-x-1 bg-zinc-900 rounded-xl p-1 mb-8 overflow-x-auto" data-testid="admin-tabs">
          {['overview', 'global', 'overrides', 'verifications'].map(t => (
            <button key={t} onClick={() => setActiveTab(t)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all capitalize whitespace-nowrap ${activeTab === t ? 'bg-zinc-800 text-white' : 'text-zinc-500'}`}
              data-testid={`admin-tab-${t}`}>
              {t === 'overrides' ? 'By Industry/Location' : t === 'verifications' ? 'ID Verification' : t}
            </button>
          ))}
        </div>

        {/* Overview */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="stats-section">
              {[
                { label: 'Users', value: stats.total_users, icon: Users, color: 'text-blue-400' },
                { label: 'Posts', value: stats.total_posts, icon: TrendingUp, color: 'text-green-400' },
                { label: 'Bookings', value: stats.total_bookings, icon: Calendar, color: 'text-purple-400' },
                { label: 'DMs', value: stats.total_dms, icon: MessageSquare, color: 'text-orange-400' },
                { label: 'Stories', value: stats.total_stories, icon: Globe, color: 'text-cyan-400' },
              ].map(s => (
                <div key={s.label} className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
                  <s.icon className={`w-6 h-6 ${s.color} mb-3`} />
                  <p className="text-2xl font-bold">{s.value || 0}</p>
                  <p className="text-xs text-zinc-500">{s.label}</p>
                </div>
              ))}
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h3 className="font-bold mb-4 flex items-center"><Settings className="w-5 h-5 mr-2 text-zinc-400" />Current Rates</h3>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                {FIELDS.map(f => (
                  <div key={f.key} className="bg-zinc-800 rounded-lg p-4 text-center">
                    <f.icon className="w-5 h-5 text-zinc-500 mx-auto mb-2" />
                    <p className="text-xs text-zinc-500 mb-1">{f.label}</p>
                    <p className="text-xl font-bold">{commissions[f.key] ?? 15}%</p>
                  </div>
                ))}
                <div className="bg-zinc-800 rounded-lg p-4 text-center">
                  <TrendingUp className="w-5 h-5 text-yellow-400 mx-auto mb-2" />
                  <p className="text-xs text-zinc-500 mb-1">Referral %</p>
                  <p className="text-xl font-bold">{commissions.referral_percentage ?? 5}%</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Global Commissions */}
        {activeTab === 'global' && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 max-w-xl" data-testid="global-commissions">
            <h2 className="font-bold text-lg mb-1">Global Commissions</h2>
            <p className="text-xs text-zinc-500 mb-6">Default platform cut for all interactions</p>
            <div className="space-y-3">
              {FIELDS.map(f => (
                <div key={f.key} className="flex items-center justify-between bg-zinc-800 rounded-xl px-4 py-3">
                  <div className="flex items-center space-x-3">
                    <f.icon className={`w-5 h-5 ${f.color}`} />
                    <span className="text-sm">{f.label}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <input type="number" min="0" max="100" step="0.5" value={commissions[f.key] ?? 15}
                      onChange={e => setCommissions({ ...commissions, [f.key]: parseFloat(e.target.value) || 0 })}
                      className="w-20 bg-zinc-700 rounded-lg px-3 py-2 text-sm text-right outline-none" data-testid={`input-${f.key}`} />
                    <span className="text-sm text-zinc-400">%</span>
                  </div>
                </div>
              ))}
              {/* Referral Rate */}
              <div className="flex items-center justify-between bg-zinc-800 rounded-xl px-4 py-3">
                <div className="flex items-center space-x-3">
                  <TrendingUp className="w-5 h-5 text-yellow-400" />
                  <span className="text-sm">Referral Reward</span>
                </div>
                <div className="flex items-center space-x-1">
                  <input type="number" min="0" max="50" step="0.5" value={commissions.referral_percentage ?? 5}
                    onChange={e => setCommissions({ ...commissions, referral_percentage: parseFloat(e.target.value) || 0 })}
                    className="w-20 bg-zinc-700 rounded-lg px-3 py-2 text-sm text-right outline-none" data-testid="input-referral" />
                  <span className="text-sm text-zinc-400">%</span>
                </div>
              </div>
              <button onClick={saveGlobal} disabled={saving}
                className="w-full bg-white text-black font-bold rounded-full py-3 text-sm mt-4 disabled:opacity-50 flex items-center justify-center space-x-2"
                data-testid="save-global-btn"><Save className="w-4 h-4" /><span>{saving ? 'Saving...' : 'Save Global Rates'}</span></button>
            </div>
          </div>
        )}

        {/* Industry/Location Overrides */}
        {activeTab === 'overrides' && (
          <div className="space-y-4" data-testid="overrides-section">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-bold text-lg">Commission Overrides</h2>
                <p className="text-xs text-zinc-500">Set different rates by industry and/or location</p>
              </div>
              <button onClick={() => setShowAddOverride(true)}
                className="bg-white text-black font-bold rounded-full px-4 py-2 text-sm flex items-center space-x-1"
                data-testid="add-override-btn"><Plus className="w-4 h-4" /><span>Add Override</span></button>
            </div>
            {overrides.length === 0 ? (
              <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
                <Globe className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
                <p className="text-sm text-zinc-500">No overrides yet. Global rates apply everywhere.</p>
              </div>
            ) : (
              overrides.map((o, i) => (
                <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="font-bold text-sm">{o.industry}</span>
                      {o.country && <span className="text-xs text-zinc-400 ml-2">({o.country})</span>}
                    </div>
                    <button onClick={() => deleteOverride(o)} className="text-zinc-500 hover:text-red-400"><X className="w-4 h-4" /></button>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {FIELDS.map(f => (
                      <div key={f.key} className="bg-zinc-800 rounded-lg p-2 text-center">
                        <p className="text-[10px] text-zinc-500">{f.label}</p>
                        <p className="text-sm font-bold">{o[f.key] ?? '-'}%</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
            {/* Add Override Modal */}
            {showAddOverride && (
              <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center" onClick={() => setShowAddOverride(false)}>
                <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="add-override-modal">
                  <h3 className="font-bold text-lg mb-4">New Commission Override</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-zinc-400 mb-1">Industry *</label>
                      <input type="text" value={newOverride.industry} onChange={e => setNewOverride({ ...newOverride, industry: e.target.value })}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-sm outline-none" placeholder="e.g., Finance" />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-400 mb-1">Country (optional)</label>
                      <input type="text" value={newOverride.country} onChange={e => setNewOverride({ ...newOverride, country: e.target.value })}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-sm outline-none" placeholder="e.g., India" />
                    </div>
                    {FIELDS.map(f => (
                      <div key={f.key} className="flex items-center justify-between">
                        <span className="text-sm text-zinc-400">{f.label}</span>
                        <div className="flex items-center space-x-1">
                          <input type="number" min="0" max="100" value={newOverride[f.key]}
                            onChange={e => setNewOverride({ ...newOverride, [f.key]: e.target.value })}
                            className="w-20 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-sm outline-none text-right" placeholder="15" />
                          <span className="text-xs text-zinc-500">%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button onClick={addOverride} className="w-full bg-white text-black font-bold rounded-full py-3 text-sm mt-4" data-testid="save-override-btn">Save Override</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ID Verification */}
        {activeTab === 'verifications' && (
          <div className="space-y-6" data-testid="verifications-section">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              {[
                { label: 'Verified', value: verifyStats.verified || 0, color: 'text-green-400', bg: 'bg-green-500/10' },
                { label: 'Pending', value: verifyStats.pending || 0, color: 'text-amber-400', bg: 'bg-amber-500/10' },
                { label: 'Rejected', value: verifyStats.rejected || 0, color: 'text-red-400', bg: 'bg-red-500/10' },
              ].map(s => (
                <div key={s.label} className={`${s.bg} border border-zinc-800 rounded-xl p-5`}>
                  <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-xs text-zinc-500">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Pending list */}
            <div>
              <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-400" /> Pending Verifications
              </h2>
              {pendingVerifications.length === 0 ? (
                <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
                  <Check className="w-10 h-10 text-green-500/30 mx-auto mb-2" />
                  <p className="text-sm text-zinc-500">No pending verifications</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingVerifications.map(u => (
                    <div key={u.user_id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid={`verify-${u.user_id}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center overflow-hidden shrink-0">
                          {u.picture ? <img src={u.picture} alt="" className="w-full h-full object-cover" /> :
                            <span className="text-sm font-bold">{u.name?.[0]}</span>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-white truncate">{u.name}</p>
                          <p className="text-[10px] text-zinc-500">{u.email} · @{u.username}</p>
                          <p className="text-[9px] text-zinc-600">Submitted: {u.identity_submitted_at ? new Date(u.identity_submitted_at).toLocaleString() : 'N/A'}</p>
                        </div>
                      </div>
                      {/* Document preview */}
                      {u.identity_doc_path && (
                        <div className="mb-3">
                          <button onClick={() => setPreviewDoc(previewDoc === u.user_id ? null : u.user_id)}
                            className="text-xs text-blue-400 hover:underline flex items-center gap-1" data-testid={`preview-doc-${u.user_id}`}>
                            <Eye className="w-3 h-3" /> {previewDoc === u.user_id ? 'Hide Document' : 'Preview Document'}
                          </button>
                          {previewDoc === u.user_id && (
                            <div className="mt-2 rounded-xl overflow-hidden border border-zinc-700 bg-zinc-800">
                              <img src={`${API_URL}/api/storage/${u.identity_doc_path}`} alt="ID Document" className="w-full max-h-64 object-contain" />
                            </div>
                          )}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <button onClick={() => approveUser(u.user_id)}
                          className="flex-1 bg-green-600 hover:bg-green-500 text-white font-bold rounded-xl py-2.5 text-sm flex items-center justify-center gap-1.5 transition-colors"
                          data-testid={`approve-${u.user_id}`}>
                          <Check className="w-4 h-4" /> Approve
                        </button>
                        <button onClick={() => rejectUser(u.user_id)}
                          className="flex-1 bg-red-600/20 border border-red-600/30 hover:bg-red-600/30 text-red-400 font-bold rounded-xl py-2.5 text-sm flex items-center justify-center gap-1.5 transition-colors"
                          data-testid={`reject-${u.user_id}`}>
                          <XCircle className="w-4 h-4" /> Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
